import { useEffect, useRef } from "react";
import { Button, Screen, Spinner, Text, useDelayedFlag } from "@ferrivia/ui";
import { useSignIn, useTracker, type SessionCredentials } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import s from "./LoginScreen.module.css";

export interface LoginScreenProps {
  initialErrorCode?: string | null;
  onSignedIn: (credentials: SessionCredentials) => void;
}

export function LoginScreen({ initialErrorCode = null, onSignedIn }: LoginScreenProps) {
  const { t } = useTranslation("clinic");
  const tracker = useTracker();
  const { pending, error, signIn } = useSignIn({ onSignedIn, initialErrorCode, track: tracker });
  // Delayed so a fast (cache-warm) sign-in never shows it — only a cold-start
  // stall that outlasts the delay does.
  const showSpinner = useDelayedFlag(pending, 400);

  // A ref, not a dep: tracker's identity can change but this view event
  // must still fire exactly once per mount, not once per identity change.
  const trackerRef = useRef(tracker);
  trackerRef.current = tracker;

  useEffect(() => {
    trackerRef.current.track("signin.screen_viewed", {});
  }, []);

  return (
    <Screen className={s.root}>
      <div className={s.panel}>
        <Text as="h1" size="3xl" className={s.name}>
          {t("loginScreen.heading", "Ferrivia Clinic")}
        </Text>
        <Button className={s.submit} disabled={pending} onClick={signIn}>
          {showSpinner ? <Spinner size={16} /> : null}
          {t("loginScreen.submit", "Sign in")}
        </Button>
        {error ? (
          <Text as="p" size="sm" className={s.error}>
            {error}
          </Text>
        ) : null}
      </div>
    </Screen>
  );
}
